import PropTypes from 'prop-types';
import { useMemo } from 'react';
// material
import { CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
// hooks
import useSettings from 'hooks/useSettings';
import useTheme from 'hooks/useTheme';
//
import shape from './shape';
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';

// ----------------------------------------------------------------------

ThemeConfig.propTypes = {
  children: PropTypes.node
};

export default function ThemeConfig({ children }) {
  const { themeMode, themeDirection } = useSettings();
  const { activeTheme } = useTheme();

  // Use theme from DB if available, otherwise use settings
  const themeConfig = activeTheme?.config || {};
  const modeFromTheme = themeConfig.mode || themeMode;
  const isLight = modeFromTheme === 'light';

  // Helper function to ensure color values are strings
  const ensureStringColor = (color) => {
    if (typeof color === 'string') return color;
    if (Array.isArray(color)) return color[0];
    return '#FFFFFF'; // fallback
  };

  // Merge theme config with defaults
  const themePalette = useMemo(() => {
    if (activeTheme?.config?.colors) {
      const themeColors = activeTheme.config.colors;
      const defaultPalette = isLight ? palette.light : palette.dark;

      // Ensure primary has the correct structure
      let primaryColor = defaultPalette.primary;
      if (themeColors.primary) {
        const themePrimary = themeColors.primary;
        primaryColor = {
          lighter: ensureStringColor(themePrimary.lighter) || defaultPalette.primary.lighter,
          light: ensureStringColor(themePrimary.light) || defaultPalette.primary.light,
          main: ensureStringColor(themePrimary.main) || defaultPalette.primary.main,
          dark: ensureStringColor(themePrimary.dark) || defaultPalette.primary.dark,
          darker: ensureStringColor(themePrimary.darker) || defaultPalette.primary.darker,
          contrastText: ensureStringColor(themePrimary.contrastText) || defaultPalette.primary.contrastText
        };
      }

      // Ensure background colors are strings
      let backgroundColors = defaultPalette.background;
      if (themeColors.background) {
        backgroundColors = {
          default: ensureStringColor(themeColors.background.default) || defaultPalette.background.default,
          paper: ensureStringColor(themeColors.background.paper) || defaultPalette.background.paper,
          neutral: ensureStringColor(themeColors.background.neutral) || defaultPalette.background.neutral
        };
      }

      // Ensure text colors are strings
      let textColors = defaultPalette.text;
      if (themeColors.text) {
        textColors = {
          primary: ensureStringColor(themeColors.text.primary) || defaultPalette.text.primary,
          secondary: ensureStringColor(themeColors.text.secondary) || defaultPalette.text.secondary,
          disabled: ensureStringColor(themeColors.text.disabled) || defaultPalette.text.disabled
        };
      }

      return {
        ...defaultPalette,
        mode: isLight ? 'light' : 'dark',
        primary: primaryColor,
        background: backgroundColors,
        text: textColors,
      };
    }
    return isLight ? { ...palette.light, mode: 'light' } : { ...palette.dark, mode: 'dark' };
  }, [activeTheme, isLight]);

  const themeTypography = useMemo(() => {
    if (activeTheme?.config?.typography) {
      const customFontFamily = themeConfig.typography.fontFamily || typography.fontFamily;
      
      // Ensure fontSize is a number, not an array
      let customFontSize = themeConfig.typography.fontSize;
      if (Array.isArray(customFontSize)) {
        customFontSize = customFontSize[0];
      }
      if (typeof customFontSize !== 'number' || customFontSize < 10 || customFontSize > 20) {
        customFontSize = 14; // default fallback
      }

      // Apply fontFamily to all typography variants
      const updatedTypography = {
        ...typography,
        fontFamily: customFontFamily,
        fontSize: customFontSize, // MUI uses this as base font size
      };

      // Update all typography variants to use the custom fontFamily
      Object.keys(updatedTypography).forEach((key) => {
        if (key !== 'fontFamily' && key !== 'fontSize' && typeof updatedTypography[key] === 'object') {
          updatedTypography[key] = {
            ...updatedTypography[key],
            fontFamily: customFontFamily,
          };
        }
      });

      return updatedTypography;
    }
    return typography;
  }, [activeTheme, themeConfig]);

  const themeShape = useMemo(() => {
    if (activeTheme?.config?.borderRadius !== undefined) {
      // Ensure borderRadius is a number, not an array
      let borderRadiusValue = themeConfig.borderRadius;
      if (Array.isArray(borderRadiusValue)) {
        borderRadiusValue = borderRadiusValue[0];
      }
      if (typeof borderRadiusValue !== 'number' || borderRadiusValue < 0 || borderRadiusValue > 16) {
        borderRadiusValue = shape.borderRadius; // default fallback
      }
      
      return {
        ...shape,
        borderRadius: borderRadiusValue,
      };
    }
    return shape;
  }, [activeTheme, themeConfig]);

  const themeOptions = useMemo(
    () => ({
      palette: themePalette,
      shape: themeShape,
      typography: themeTypography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark,
      iconSize: themeConfig.iconSize || 20
    }),
    [themePalette, themeShape, themeTypography, themeDirection, isLight, themeConfig]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
